import React from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import NotesIcon from '@mui/icons-material/Notes';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Chip from '@mui/material/Chip';
import { getEventTypeConfig, formatEventDateTime } from '../utils/eventTypes';
import { UI_TIMINGS } from '../../../config/appConfig';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';

/**
 * EventListItem - Displays a single event in the list
 * @param {Object} event - Event data
 * @param {Function} onClick - Callback when item is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {Function} onToggleAssignment - Callback when assignment toggle button is clicked
 * @param {boolean} disabled - Whether the item is disabled (e.g., during loading)
 * @param {React.ReactNode} expandButton - Optional expand button for completed assignments
 * @param {boolean} showDelete - Whether to show the delete button
 */
function EventListItem({ event, onClick, onDelete, onToggleAssignment, disabled = false, expandButton = null, showDelete = true }) {
  const [swipeX, setSwipeX] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const startX = React.useRef(0);
  const currentX = React.useRef(0);

  const config = getEventTypeConfig(event.eventType);
  const Icon = config.icon;
  const dateTimeStr = formatEventDateTime(event.createdAt);

  // Check if event has assignmentEventId
  const hasAssignment = !!event.metadata?.assignmentEventId;

  // For notes, use first line as primary, rest as secondary
  let primary = event.description;
  let secondary = dateTimeStr;

  if (event.eventType === 'note' && event.description) {
    const lines = event.description.split('\n');
    if (lines.length > 1) {
      primary = lines[0];
      secondary = `${dateTimeStr} • ${lines.slice(1).join(' ').substring(0, 50)}${lines.slice(1).join(' ').length > 50 ? '...' : ''}`;
    }
  }

  // For group_match, show person names only
  if (event.eventType === 'group_match') {
    const personNames = event.metadata?.personNames || [];
    const displayNames = personNames.slice(0, 2).join(', ') + (personNames.length > 2 ? ` +${personNames.length - 2}` : '');
    primary = displayNames;
    secondary = dateTimeStr;
  }

  // For assignment, append last 4 chars of eventId to primary text
  if (event.eventType === 'assignment') {
    const idSuffix = event.eventId ? event.eventId.slice(-4) : '';
    primary = `${event.description} (${idSuffix})`;
  }

  const handleDeleteClick = React.useCallback((e) => {
    e.stopPropagation(); // Prevent triggering onClick when delete is clicked
    onDelete(event);
  }, [onDelete, event]);

  const handleClick = React.useCallback(() => {
    if (!isSwiping) {
      onClick(event);
    }
  }, [onClick, event, isSwiping]);

  // Get stats from metadata if available (populated by backend or context)
  const assignmentStats = event.eventType === 'assignment' && event.status === 'closed' && event.metadata?.stats
    ? event.metadata.stats
    : null;

  const [isMouseDown, setIsMouseDown] = React.useState(false);

  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
    setIsSwiping(false);
  };

  const handleMove = (clientX) => {
    currentX.current = clientX;
    const diff = currentX.current - startX.current;

    // Only allow left swipe (negative values)
    if (diff < 0) {
      setSwipeX(Math.max(diff, -100)); // Limit to -100px
      if (Math.abs(diff) > 10) {
        setIsSwiping(true);
      }
    }
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;

    // If swiped more than 60px, trigger delete
    if (diff < -60) {
      onDelete(event);
    } else {
      // Reset position
      setSwipeX(0);
      setTimeout(() => setIsSwiping(false), UI_TIMINGS.SWIPE_ANIMATION_CLEANUP);
    }
    setIsMouseDown(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isMouseDown) {
      handleMove(e.clientX);
    }
  };

  const handleMouseUp = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown) {
      handleEnd();
    }
  };

  // Get background color based on event type (fully opaque for swipe background)
  const getBackgroundColor = () => {
    if (event.eventType === 'assignment') {
      return '#eceff1'; // Cool grey (matches aggregator UI)
    } else if (event.eventType === 'note') {
      return '#fff8e1'; // Pale amber - fully opaque
    } else if (event.eventType === 'group_match') {
      return '#e3f2fd'; // Light blue - fully opaque
    } else if (event.metadata?.assignmentEventId) {
      return '#f5f5f5'; // Light grey for events within assignments (matches aggregator UI)
    }
    return '#fff';
  };

  const handleToggleAssignmentClick = React.useCallback((e) => {
    e.stopPropagation(); // Prevent triggering onClick
    if (onToggleAssignment) {
      onToggleAssignment(event.eventId);
    }
  }, [onToggleAssignment, event.eventId]);

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 1,
        overflow: 'hidden',
        borderRadius: 1
      }}
    >
      {/* Delete background - revealed during swipe */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 100,
          backgroundColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          zIndex: 0
        }}
      >
        <DeleteIcon />
      </Box>

      {/* Main list item - slides left on swipe */}
      <ListItem
        disablePadding
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          backgroundColor: `${getBackgroundColor()} !important`,
          borderRadius: 1,
          borderLeft: event.eventType === 'assignment'
            ? '6px solid #ff9800'
            : event.eventType === 'group_match'
            ? '6px solid #1976d2'
            : event.metadata?.assignmentEventId
            ? '4px solid #ffcc80'
            : 'none',
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          cursor: isMouseDown ? 'grabbing' : 'grab',
          zIndex: 1,
          '& .MuiListItemSecondaryAction-root': {
            right: 8
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: getBackgroundColor(),
            zIndex: -1
          }
        }}
        secondaryAction={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Toggle button for new-style assignments */}
            {event.eventType === 'assignment' && onToggleAssignment && (
              <IconButton
                edge="end"
                aria-label={event.status === 'open' ? 'close assignment' : 'reopen assignment'}
                size="small"
                onClick={handleToggleAssignmentClick}
                disabled={disabled}
                sx={{ color: event.status === 'open' ? 'success.main' : 'primary.main' }}
              >
                {event.status === 'open' ? <ToggleOnIcon /> : <ToggleOffIcon />}
              </IconButton>
            )}
            {/* Assignment indicator above expand button in vertical column */}
            {(event.metadata?.assignmentEventId || expandButton) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
                {event.metadata?.assignmentEventId && (
                  <Tooltip title={event.metadata.assignmentEventId} arrow>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: -0.5 }}>
                      <AssignmentIcon sx={{ fontSize: 16, color: '#1976d2', opacity: 0.7 }} />
                    </Box>
                  </Tooltip>
                )}
                {expandButton}
              </Box>
            )}
            {false && showDelete && (
              <IconButton
                edge="end"
                aria-label="delete"
                size="small"
                onClick={handleDeleteClick}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      >
        <ListItemButton
          onClick={handleClick}
          disabled={disabled}
          disableRipple={isSwiping}
          sx={{
            backgroundColor: 'inherit',
            '&:hover': {
              backgroundColor: 'inherit'
            },
            '& .MuiTouchRipple-root': {
              display: 'none'
            },
            display: 'flex',
            gap: 1
          }}
        >
          {/* 1. Icon or captured image */}
          {(event.eventType === 'group_match' || event.eventType === 'detection') && event.metadata?.capturedImageUrl ? (
            <Box
              component="img"
              src={getCapturedImageDisplayUrl(event.metadata.capturedImageUrl)}
              alt={event.eventType === 'group_match' ? 'Group capture' : 'Detection capture'}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: 1,
                flexShrink: 0
              }}
            />
          ) : (
            <Box sx={{ color: config.color, flexShrink: 0 }}>
              <Icon fontSize="small" />
            </Box>
          )}

          {/* 2. Title and timestamp */}
          <ListItemText
            primary={primary}
            secondary={secondary}
            sx={{ flex: 1, minWidth: 0 }}
          />

          {/* 3. Stats summary (for assignments) and statement indicator (for match events) */}
          {assignmentStats && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              flexShrink: 0,
              alignItems: 'flex-end',
              ml: 1
            }}>
              {assignmentStats.noteCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {assignmentStats.noteCount}
                  </Typography>
                </Box>
              )}
              {assignmentStats.aiMatches > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    {assignmentStats.aiMatches}
                  </Typography>
                </Box>
              )}
              {assignmentStats.humanMatches > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {assignmentStats.humanMatches}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {/* Statement indicator for match events */}
          {(event.eventType === 'match' || event.eventType === 'soi_match') && event.metadata?.statementCount > 0 && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              flexShrink: 0,
              alignItems: 'flex-end',
              ml: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EditNoteIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                <Typography variant="caption" color="text.secondary">
                  {event.metadata.statementCount}
                </Typography>
              </Box>
            </Box>
          )}
        </ListItemButton>
      </ListItem>
    </Box>
  );
}

export default React.memo(EventListItem);
